import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { GroupDto } from '@/api/types'
import {
  groupsQuery,
  permissionCategoriesQuery,
  useDeleteGroup,
  useUpdateGroup,
} from '@/features/platform/platform.queries'
import { GroupFormDialog } from '@/features/groups/GroupFormDialog'
import { GroupMembers } from '@/features/groups/GroupMembers'
import { holdsPermission, PERMISSIONS, usePermission } from '@/features/auth/permissions'
import {
  ActionMenu,
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  TabPanel,
  Tabs,
  useToast,
} from '@/ui'
import { IconAdd, IconDelete, IconEdit, IconGroups, IconLock, IconSave } from '@/icons'

export const Route = createFileRoute('/_authed/groups/')({
  component: GroupsPage,
})

function GroupsPage() {
  const { t } = useTranslation()
  const toast = useToast()

  const canGrant = usePermission(PERMISSIONS.PERMISSION_GRANT)
  const canManage = usePermission(PERMISSIONS.ROLE_MANAGE)

  const groups = useQuery(groupsQuery)
  const categories = useQuery(permissionCategoriesQuery)
  const update = useUpdateGroup()
  const remove = useDeleteGroup()

  const [selectedId, setSelectedId] = useState<string | null>(null)
  /** The in-progress edit. Null means "showing what the server has". */
  const [draft, setDraft] = useState<Set<string> | null>(null)
  const [editing, setEditing] = useState<GroupDto | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [deleting, setDeleting] = useState<GroupDto | null>(null)
  const [tab, setTab] = useState<'permissions' | 'members'>('permissions')

  const platformGroups = useMemo(
    () => (groups.data?.items ?? []).filter((group) => group.worldId === null),
    [groups.data],
  )

  const selected = platformGroups.find((group) => group.id === selectedId) ?? platformGroups[0]
  const held = draft ?? new Set(selected?.permissions ?? [])
  const isDirty = draft !== null

  function toggle(key: string, isHeld: boolean) {
    const next = new Set(held)
    if (isHeld) next.add(key)
    else next.delete(key)
    setDraft(next)
  }

  function save() {
    if (!selected || !draft) return

    update.mutate(
      { groupId: selected.id, permissions: [...draft] },
      {
        onSuccess: () => {
          toast.success(t('groups.updated'))
          setDraft(null)
        },
        onError: (error) => {
          toast.fromError(error, t('state.errorTitle'))
        },
      },
    )
  }

  if (groups.isPending || categories.isPending) return <LoadingState />
  if (groups.isError) {
    return (
      <ErrorState
        error={groups.error}
        onRetry={() => {
          void groups.refetch()
        }}
      />
    )
  }

  return (
    <>
      <PageHeader
        title={t('groups.title')}
        description={t('groups.subtitle')}
        actions={
          isDirty ? (
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setDraft(null)
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                icon={<IconSave size={15} />}
                isLoading={update.isPending}
                onClick={save}
              >
                {t('common.save')}
              </Button>
            </>
          ) : canManage ? (
            <Button
              variant="primary"
              icon={<IconAdd size={15} />}
              onClick={() => {
                setIsCreating(true)
              }}
            >
              {t('groups.add')}
            </Button>
          ) : undefined
        }
      />

      {platformGroups.length === 0 ? (
        <Card>
          <EmptyState
            title={t('groups.empty')}
            icon={<IconGroups size={22} />}
            action={
              canManage ? (
                <Button
                  variant="primary"
                  size="sm"
                  icon={<IconAdd size={14} />}
                  onClick={() => {
                    setIsCreating(true)
                  }}
                >
                  {t('groups.add')}
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
          <Card isFlush className="h-fit">
            <div className="p-4 pb-2">
              <CardHeader title={t('groups.platformGroups')} as="h2" />
            </div>
            <ul className="p-2">
              {platformGroups.map((group) => (
                <li key={group.id}>
                  <button
                    type="button"
                    aria-current={group.id === selected?.id ? 'true' : undefined}
                    onClick={() => {
                      setSelectedId(group.id)
                      // Switching group discards an unsaved edit rather than carrying it
                      // across, which would silently write one group's rights onto another.
                      setDraft(null)
                    }}
                    className={
                      group.id === selected?.id
                        ? 'flex w-full items-center gap-2 rounded-control bg-primary-subtle px-2.5 py-2 text-left text-[0.8125rem] font-medium text-primary'
                        : 'flex w-full items-center gap-2 rounded-control px-2.5 py-2 text-left text-[0.8125rem] text-text-secondary transition-colors hover:bg-surface-sunken hover:text-text'
                    }
                  >
                    <span
                      aria-hidden
                      className="size-2 shrink-0 rounded-full"
                      style={{ background: group.colorHex ?? 'var(--color-border-strong)' }}
                    />
                    <span className="min-w-0 flex-1 truncate">{group.name}</span>
                    <span data-numeric className="shrink-0 text-xs text-text-muted">
                      {group.memberCount}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          {selected ? (
            <div>
              <div className="mb-4 flex items-start justify-between gap-3">
                <Tabs
                  tabs={[
                    { id: 'permissions', label: t('groups.permissions') },
                    { id: 'members', label: t('groups.members'), count: selected.memberCount },
                  ]}
                  activeId={tab}
                  onChange={(next) => {
                    setTab(next === 'members' ? 'members' : 'permissions')
                  }}
                  className="flex-1"
                />

                {canManage && !selected.isSystem ? (
                  <ActionMenu
                    actions={[
                      {
                        id: 'edit',
                        label: t('groups.editTitle'),
                        icon: <IconEdit size={15} />,
                        onSelect: () => {
                          setEditing(selected)
                        },
                      },
                      {
                        id: 'delete',
                        label: t('common.delete'),
                        icon: <IconDelete size={15} />,
                        isDestructive: true,
                        onSelect: () => {
                          setDeleting(selected)
                        },
                      },
                    ]}
                  />
                ) : null}
              </div>

              <TabPanel id={tab}>
                {tab === 'permissions' ? (
                  <PermissionMatrix
                    group={selected}
                    held={held}
                    categories={categories.data?.items ?? []}
                    canEdit={canGrant}
                    onToggle={toggle}
                  />
                ) : (
                  <GroupMembers group={selected} />
                )}
              </TabPanel>
            </div>
          ) : null}
        </div>
      )}

      <GroupFormDialog
        key={editing?.id ?? "new"}
        isOpen={isCreating || editing !== null}
        onClose={() => {
          setIsCreating(false)
          setEditing(null)
        }}
        group={editing}
      />

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => {
          setDeleting(null)
        }}
        onConfirm={() => {
          if (!deleting) return
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast.success(t('groups.deleted'))
              // The deleted group was the selection; fall back to the first rather than
              // leaving the panel pointed at something that no longer exists.
              setSelectedId(null)
              setDeleting(null)
            },
            onError: (error) => {
              toast.fromError(error, t('state.errorTitle'))
            },
          })
        }}
        title={t('groups.deleteConfirm')}
        body={t('groups.deleteBody', {
          name: deleting?.name ?? '',
          count: deleting?.memberCount ?? 0,
        })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        isPending={remove.isPending}
      />
    </>
  )
}

function PermissionMatrix({
  group,
  held,
  categories,
  canEdit,
  onToggle,
}: {
  group: GroupDto
  held: Set<string>
  categories: readonly {
    key: string
    name: string
    description: string
    permissions: readonly {
      key: string
      description: string
      planGated: boolean
    }[]
  }[]
  canEdit: boolean
  onToggle: (key: string, isHeld: boolean) => void
}) {
  const { t } = useTranslation()

  return (
    <Card isFlush>
      <div className="flex items-start justify-between gap-4 border-b border-border p-5">
        <CardHeader
          title={group.name}
          description={group.description}
          as="h2"
          actions={group.isSystem ? <Badge tone="info">{t('groups.badgeSystem')}</Badge> : undefined}
        />
      </div>

      {group.isSystem ? (
        <p className="flex items-start gap-2 border-b border-border bg-info-subtle px-5 py-2.5 text-xs leading-4 text-info">
          <IconLock size={14} className="mt-px shrink-0" />
          {t('groups.systemHint')}
        </p>
      ) : null}

      <div className="divide-y divide-border">
        {categories.map((category) => {
          /*
           * A wildcard the group holds covers keys that are not literally in the set — which is
           * the whole point of Administrator's `*`. Counting only literal entries reported
           * "6 of 12" for a group that in fact holds all twelve, and every uncovered box looked
           * like a capability it lacked.
           */
          const heldList = [...held]
          const covered = category.permissions.filter((permission) =>
            holdsPermission(heldList, permission.key),
          ).length

          return (
            <section key={category.key} className="p-5">
              <div className="mb-3 flex items-baseline justify-between gap-4">
                <div>
                  <h3 className="text-[0.8125rem] leading-5 font-semibold text-text">
                    {category.name}
                  </h3>
                  <p className="text-xs leading-4 text-text-secondary">{category.description}</p>
                </div>
                <span data-numeric className="shrink-0 text-xs text-text-muted">
                  {t('groups.permissionsHeld', {
                    count: covered,
                    total: category.permissions.length,
                  })}
                </span>
              </div>

              <ul className="grid gap-2.5 sm:grid-cols-2">
                {category.permissions.map((permission) => {
                  const isLiteral = held.has(permission.key)
                  // Covered by a wildcard rather than named: shown as held, but not something
                  // you can untick — removing it means removing the wildcard.
                  const isImplied = !isLiteral && holdsPermission(heldList, permission.key)

                  return (
                  <li key={permission.key}>
                    <Checkbox
                      label={
                        <span className="flex items-center gap-1.5">
                          {/* No display name on a permission — the key IS the name, and it is
                              what an operator recognises from the backend catalogue. */}
                          <code className="font-mono text-xs">{permission.key}</code>
                          {permission.planGated ? (
                            <Badge tone="warning">{t('groups.planGated')}</Badge>
                          ) : null}
                        </span>
                      }
                      description={
                        isImplied ? t('groups.impliedByWildcard') : permission.description
                      }
                      isChecked={isLiteral || isImplied}
                      isDisabled={!canEdit || isImplied}
                      onChange={(next) => {
                        onToggle(permission.key, next)
                      }}
                    />
                  </li>
                  )
                })}
              </ul>
            </section>
          )
        })}
      </div>
    </Card>
  )
}
